import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PackagePlus, ScanLine, Loader2, CheckCircle2, MapPin, AlertTriangle, Ban, ChevronDown, Search, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandList, CommandItem } from '@/components/ui/command';
import { toast } from 'sonner';
import * as api from '@/services/seasonalWmsApi';

const SeasonalPackedBoxPutAway = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [scan, setScan] = useState(null);      // scan result
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locCode, setLocCode] = useState('');        // scanned bin label
  const [scannedLoc, setScannedLoc] = useState(null); // resolved bin
  const [locLoading, setLocLoading] = useState(false);
  const [lastStored, setLastStored] = useState(null);
  const [locations, setLocations] = useState([]);    // position bins for the selector
  const [loadingLocs, setLoadingLocs] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [locSearch, setLocSearch] = useState('');
  const inputRef = useRef(null);
  const locRef = useRef(null);

  const loadLocations = useCallback(async () => {
    setLoadingLocs(true);
    try {
      const r = await api.getLocations('position');
      setLocations(r.locations || []);
    } catch { /* selector is optional; scanning still works */ }
    finally { setLoadingLocs(false); }
  }, []);

  const doScan = useCallback(async (value) => {
    const c = (value ?? code).trim();
    if (!c) return;
    setLoading(true); setScan(null); setScannedLoc(null); setLocCode(''); setLocSearch('');
    try {
      const res = await api.scanBox(c);
      setScan(res);
      if (!res.already_stored) {
        setTimeout(() => locRef.current?.focus(), 50);
        loadLocations();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Box not found');
    } finally { setLoading(false); }
  }, [code, loadLocations]);

  const doScanLocation = useCallback(async (value) => {
    const v = (value ?? locCode).trim();
    if (!v || !scan?.box) return;
    setLocLoading(true);
    try {
      const res = await api.resolveLocation(v, scan.box.box_number);
      setScannedLoc(res.location);
      if (res.warning) toast.warning(res.warning);
      else toast.success(`Bin ${res.location.footprint} selected`);
    } catch (e) {
      setScannedLoc(null);
      toast.error(e.response?.data?.detail || 'Location not found');
    } finally { setLocLoading(false); }
  }, [locCode, scan]);

  // Pick a bin from the selector — reuses the exact same resolve/validation
  // path as scanning (so locked / full / capacity / wrong-customer warnings
  // still fire) by resolving the chosen bin's location_id.
  const pickLocation = useCallback((loc) => {
    setLocOpen(false);
    setLocCode(loc.footprint || '');
    doScanLocation(loc.location_id);
  }, [doScanLocation]);

  const filteredLocs = useMemo(() => {
    const cust = scan?.box?.customer || '';
    const q = locSearch.trim().toLowerCase();
    let list = locations;
    if (q) list = list.filter((l) =>
      (l.footprint || '').toLowerCase().includes(q) ||
      (l.current_customer || '').toLowerCase().includes(q));
    const rank = (l) => {
      if (l.status === 'full') return 4;
      if (cust && l.current_customer === cust) return 0;         // same customer → best
      if (!l.current_customer && (l.box_count || 0) === 0) return 1; // empty
      if (!l.current_customer) return 2;
      return 3;                                                   // holds another customer
    };
    return [...list]
      .sort((a, b) => rank(a) - rank(b) || (a.footprint || '').localeCompare(b.footprint || ''))
      .slice(0, 100);
  }, [locations, locSearch, scan]);

  const chosenLocationId = scannedLoc?.location_id || scan?.suggestion?.location_id;
  const isOverride = !!(scannedLoc && scannedLoc.location_id !== scan?.suggestion?.location_id);

  const confirm = async () => {
    if (!scan || scan.already_stored) return;
    if (!chosenLocationId) { toast.error('No bin selected. Scan a bin label or add one in Location Setup.'); return; }
    setSaving(true);
    try {
      const res = await api.putAway({
        box_code: scan.box.box_number,
        location_id: chosenLocationId,
        overridden: isOverride,
        override_reason: isOverride ? 'Operator scanned a different bin' : null,
      });
      toast.success(res.message);
      setLastStored({ box: scan.box.box_number, loc: res.location.footprint, customer: res.customer, full: res.location.became_full });
      setScan(null); setCode(''); setScannedLoc(null); setLocCode('');
      inputRef.current?.focus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Put away failed');
    } finally { setSaving(false); }
  };

  const markFull = async () => {
    const id = chosenLocationId;
    if (!id) return;
    try { const r = await api.markBinFull(id); toast.success(r.message); await doScan(scan.box.box_number); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const sug = scan?.suggestion;
  const box = scan?.box;

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="seasonal-putaway-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-3xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/wms/seasonal-bundle')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="spa-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <PackagePlus className="w-6 h-6 mr-3 text-emerald-400" />
          <div>
            <h1 className="text-xl font-bold font-display">Packed Box Put Away</h1>
            <p className="text-sm text-[#E8DCC8]">Scan a transferred box — one customer per bin</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-5">
        <Card>
          <CardContent className="pt-5">
            <label className="text-xs text-[#5A5F6E]">Scan / enter Box Number or Small-box code</label>
            <div className="flex gap-2 mt-1">
              <Input
                ref={inputRef} autoFocus value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') doScan(); }}
                placeholder="Scan box or small-box code"
                className="flex-1 font-mono"
                data-testid="spa-scan-input"
              />
              <Button onClick={() => doScan()} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="spa-scan-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {lastStored && !scan && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3" data-testid="spa-last-stored">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-emerald-800">Stored: {lastStored.box}</p>
              <p className="text-emerald-700">{lastStored.customer} → {lastStored.loc}{lastStored.full ? ' (bin now FULL)' : ''}</p>
            </div>
          </div>
        )}

        {scan && box && (
          <Card data-testid="spa-result">
            <CardContent className="pt-5 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono font-semibold text-[#1A1F2E]" data-testid="spa-box-number">{box.box_number}</span>
                {box.external_box_no && (
                  <Badge className="bg-teal-100 text-teal-700 border-0" data-testid="spa-box-smallbox">Small-box: {box.external_box_no}</Badge>
                )}
                <Badge className="bg-indigo-100 text-indigo-700 border-0" data-testid="spa-box-customer">{box.customer || '(no customer)'}</Badge>
                <Badge className="bg-gray-100 text-gray-600 border-0">Slug {box.slug || '—'}</Badge>
                <Badge className="bg-amber-100 text-amber-700 border-0">{box.total_qty} pcs · {box.item_count} items</Badge>
              </div>

              {scan.already_stored ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="spa-already-stored">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">Already put away at <b>{scan.stored_location?.footprint}</b> ({scan.stored_location?.status}).</p>
                </div>
              ) : (
                <>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="spa-suggestion">
                    <p className="text-xs text-emerald-700 mb-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Suggested bin</p>
                    {sug ? (
                      <>
                        <p className="text-lg font-bold text-emerald-900 font-mono">{sug.footprint}</p>
                        <p className="text-xs text-emerald-700 mt-0.5">{sug.reason}</p>
                      </>
                    ) : (
                      <p className="text-sm text-red-600">No available bin found. Add a bin in Location Setup, or pick one below.</p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs text-[#5A5F6E]">Scan bin location (leave blank to use the suggested bin)</label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        ref={locRef} value={locCode}
                        onChange={(e) => setLocCode(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') doScanLocation(); }}
                        placeholder="Scan bin label e.g. SH1 > GF > R1 > P1"
                        className="flex-1 font-mono"
                        data-testid="spa-location-input"
                      />
                      <Button onClick={() => doScanLocation()} disabled={locLoading} variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="spa-location-scan-btn">
                        {locLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
                      </Button>
                    </div>

                    {/* Or pick a bin from the list */}
                    <div className="flex items-center gap-3 my-3">
                      <div className="h-px bg-gray-200 flex-1" />
                      <span className="text-[11px] uppercase tracking-wide text-[#8A8F9E]">or select a bin</span>
                      <div className="h-px bg-gray-200 flex-1" />
                    </div>
                    <Popover open={locOpen} onOpenChange={setLocOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={locOpen}
                          className="w-full justify-between font-normal"
                          data-testid="spa-location-select-trigger"
                        >
                          <span className="text-[#8A8F9E] flex items-center gap-1.5">
                            <MapPin className="w-4 h-4" />
                            {loadingLocs ? 'Loading bins…' : 'Choose a bin from the list…'}
                          </span>
                          <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[340px]" align="start">
                        <Command shouldFilter={false}>
                          <div className="flex items-center border-b px-3">
                            <Search className="w-4 h-4 mr-2 shrink-0 opacity-50" />
                            <CommandInput value={locSearch} onValueChange={setLocSearch} placeholder="Search bin by footprint / customer…" className="h-10 border-0 focus:ring-0" data-testid="spa-location-select-search" />
                          </div>
                          <CommandList className="max-h-72">
                            <CommandEmpty>{loadingLocs ? 'Loading bins…' : 'No matching bin.'}</CommandEmpty>
                            <CommandGroup>
                              {filteredLocs.map((l) => {
                                const cust = scan?.box?.customer || '';
                                const isFull = l.status === 'full';
                                const sameCust = cust && l.current_customer === cust;
                                const otherCust = l.current_customer && l.current_customer !== cust;
                                const empty = !l.current_customer && (l.box_count || 0) === 0;
                                const isSel = scannedLoc?.location_id === l.location_id;
                                return (
                                  <CommandItem
                                    key={l.location_id}
                                    value={l.location_id}
                                    onSelect={() => pickLocation(l)}
                                    disabled={isFull}
                                    data-testid={`spa-location-opt-${l.location_id}`}
                                  >
                                    <Check className={`w-4 h-4 mr-2 ${isSel ? 'opacity-100 text-emerald-600' : 'opacity-0'}`} />
                                    <span className="flex-1 font-mono text-xs truncate">{l.footprint}</span>
                                    <span className="text-[11px] text-[#8A8F9E] ml-2 whitespace-nowrap">
                                      {(l.box_count || 0)}{l.capacity_boxes ? `/${l.capacity_boxes}` : ''} box
                                    </span>
                                    {isFull ? (
                                      <Badge className="bg-red-100 text-red-700 border-0 text-[10px] ml-2">FULL</Badge>
                                    ) : sameCust ? (
                                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] ml-2">same</Badge>
                                    ) : empty ? (
                                      <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px] ml-2">empty</Badge>
                                    ) : otherCust ? (
                                      <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] ml-2 max-w-[110px] truncate">{l.current_customer}</Badge>
                                    ) : null}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>

                    {scannedLoc && (
                      <div className="mt-2 bg-sky-50 border border-sky-200 rounded-lg p-3" data-testid="spa-scanned-location">
                        <p className="text-xs text-sky-700 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Scanned bin</p>
                        <p className="text-base font-bold text-sky-900 font-mono" data-testid="spa-scanned-location-footprint">{scannedLoc.footprint}</p>
                        <p className="text-xs text-sky-700 mt-0.5">
                          {scannedLoc.box_count || 0}{scannedLoc.capacity_boxes ? `/${scannedLoc.capacity_boxes}` : ''} boxes
                          {scannedLoc.current_customer ? ` · ${scannedLoc.current_customer}` : ' · empty'}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={confirm} disabled={saving || !chosenLocationId} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="spa-confirm-btn">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      Put Away Here
                    </Button>
                    {chosenLocationId && (
                      <Button onClick={markFull} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="spa-mark-full-btn">
                        <Ban className="w-4 h-4 mr-1" /> Mark Bin Full
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SeasonalPackedBoxPutAway;
